import React, { ReactElement, useRef } from "react";
import styled from "@emotion/styled";
import DropDownSelectField, { Size } from "../../../../components/forms/DropDownSelectField";
import { style_border_default } from "../../../../constants/stylesValues";
import { color_colors_ocean, color_shades_darkest, color_variants_ocean_xlight } from "../../../../constants/colors";
import { MenuRendererProps } from "react-select";
import { Option } from "../../../../models/customAssistant/CustomAssistantConfigurationsOption";
import IconTooltip from "../../../../components/general/IconTooltip";
import { PortalMenu } from "./DropdownPortalMenu";

interface StyledDropdownProps {
  handleOnChange?: (optionValue: string, value: string | boolean) => void;
  fieldName?: string;
  options?: Option[];
  size?: Size;
  value?: string;
  className?: string;
  searchable?: boolean;
  disabled?: boolean;
  label?: string | ReactElement;
  tooltip?: string;
  tooltipIcon?: string;
  tooltipPosition?: string;
  placeholder?: string;
  labelStyle?: string;
  openMenuAbove?: boolean;
  inlineTooltip?: boolean;
}

const StyledDropdown: React.FC<StyledDropdownProps> = (props) => {
  const anchorRef = useRef<HTMLDivElement>(null);

  // filter options once per render
  const getFiltered = (inputValue?: string) => {
    let options = props.options ?? [];
    if (props.searchable && inputValue?.length) {
      const q = inputValue.toLowerCase();
      options = options.filter((option: Option) => option.label.toLowerCase().includes(q));
    }
    return options;
  };

  return (
    <Wrapper ref={anchorRef} className={props.className}>
      {(props.label || (props.tooltip && !props.inlineTooltip)) && (
        <LabelWrapper className={props.labelStyle}>
          {props.label && <Label>{props.label}</Label>}
          {props.tooltip && !props.inlineTooltip && <StyledIconTooltip text={props.tooltip} icon={props.tooltipIcon} place={props.tooltipPosition} />}
        </LabelWrapper>
      )}

      <InlineRow>
        <DropDownSelectField
          placeholder={props.placeholder ? props.placeholder : "Select"}
          disabled={props.disabled}
          name={props.fieldName}
          size={props.size}
          value={props.value}
          onChange={(e: any) => {
            props.handleOnChange?.(props.fieldName!, e.value);
          }}
          options={props.options}
          clearable={false}
          searchable={props.searchable ?? false}
          menuRenderer={(menuProps: MenuRendererProps | any) => {
            const options = getFiltered(menuProps.inputValue);

            return (
              <PortalMenu anchorEl={anchorRef.current} openAbove={props?.openMenuAbove ?? false} zIndex={99999} matchWidth={true}>
                <MenuContainer role="listbox" aria-label="dropdown options">
                  <Triangle />
                  {options.map((option: Option, index: number) => (
                    <SelectOptionMedium
                      key={`${option.value}-${index}`}
                      onClick={() => menuProps.selectValue(option)}
                      role="option"
                    >
                      <CustomSelectOptionLabelMedium>
                        <div>
                          <p>{option.label}</p>
                        </div>
                      </CustomSelectOptionLabelMedium>
                    </SelectOptionMedium>
                  ))}
                </MenuContainer>
              </PortalMenu>
            );
          }}
        />
        {props.tooltip && props.inlineTooltip && (
          <StyledIconTooltip text={props.tooltip} icon={props.tooltipIcon} place={props.tooltipPosition} />
        )}
      </InlineRow>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative; /* anchor element for size/position measurements */
  display: flex;
  flex-direction: column;
`;

const InlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
`;

const LabelWrapper = styled.div`
  display: flex;
  margin-bottom: 8px;
  align-items: center;
  gap: 8px;
`;

const Label = styled.div`
  font-family: Muli;
  font-size: 14px;
  font-weight: 600;
`;

const StyledIconTooltip = styled(IconTooltip)`
  flex-shrink: 0;
`;

const MenuContainer = styled.div`
  margin-top: 4px;
  background: #fff;
  border: ${style_border_default};
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  max-height: 320px;
  overflow: auto;
`;

const Triangle = styled.div`
  display: none;
`;

const SelectOptionMedium = styled.div`
  height: 48px;
  :hover {
    background-color: ${color_variants_ocean_xlight};
    cursor: pointer;
  }
`;

const CustomSelectOptionLabelMedium = styled.div`
  div {
    display: flex;
    align-items: center;
    padding-left: 16px;
    height: 48px;
    border-bottom: ${style_border_default};
  }
  p {
    color: ${color_shades_darkest};
    font-weight: 400;
    font-size: 13px;
  }
  .option-selected {
    color: ${color_colors_ocean};
  }
`;

export default StyledDropdown;
